import { z } from 'zod';
/**
 * «Titel & Beschreibung mit KI vorschlagen» (order PR-2, 15-10) — het
 * DRAADCONTRACT tussen de backend (`src/vermarktung/ki/**`) en het CRM
 * (`pages/verkauf/vermarktung/ki-vorschlag.tsx`).
 *
 * Zod-only en client-safe, dus in BEIDE barrels: het CRM leest de modellen, de
 * codes en de antwoordvorm als WAARDE. Wat hier NIET staat: de prompt, de
 * allowlist van de bron en de redactie — die blijven in de backend en bereiken
 * geen browserbundel.
 *
 * NOOIT AUTOMATISCH OPSLAAN (order A.1/G.4): een voorstel is een antwoord, geen
 * schrijfweg. Het CRM neemt per veld over in het ENTWURF; de staf slaat zelf op.
 */
/**
 * De twee modellen (order A.1, owner-besluit O-7): Sonnet 5.5 is de standaard,
 * Opus 5.5 kan per aanroep gekozen worden («Hohe Qualität», ca. dubbele kosten).
 */
export const KI_MODELLE = ['claude-sonnet-5-5', 'claude-opus-5-5'];
export const kiModellSchema = z.enum(KI_MODELLE);
export const KI_STANDARD_MODELL = 'claude-sonnet-5-5';
/**
 * De romp van `POST /vermarktung/objekte/:id/ki-vorschlag`. `.strict()`: een
 * onbekende sleutel (of een model buiten de twee) is een 400, geen stille strip.
 * Zonder `modell` kiest de server zijn standaard (`VERMARKTUNG_KI_MODELL`).
 */
export const kiVorschlagAnfrageSchema = z.object({ modell: kiModellSchema.optional() }).strict();
/** De drie velden van een voorstel, in de volgorde van het scherm. */
export const KI_VORSCHLAG_FELDER = ['titel', 'kurzbeschreibung', 'beschreibung'];
/**
 * Waarom een voorgesteld veld NIET overgenomen kan worden (order A3):
 *  - `leer` — na de nabewerking staat er niets;
 *  - `markup` — de feed weigert markup (`enthaeltMarkup`, contract §2.3);
 *  - `zu_lang` — titel > 160 of Kurzbeschreibung > 300 (de werkbankgrenzen);
 *  - `inhalt` — de inhouds-poort (`pruefeInhalt`) vond een dossierwaarde; `bron`
 *    noemt welke (`eigentuemerName`, …), nooit de waarde zelf.
 */
export const KI_SPERRE_CODES = ['leer', 'markup', 'zu_lang', 'inhalt'];
export const kiSperreSchema = z
    .object({ code: z.enum(KI_SPERRE_CODES), bron: z.string().min(1).max(60).optional() })
    .strict();
/**
 * Wat de staf NAKIJKT voor hij overneemt (order A3) — geen sperre:
 *  - `titel_lang` — titel > 70 tekens;
 *  - `beschreibung_kurz` / `beschreibung_lang` — buiten 900–1'500 tekens;
 *  - `markdown` — opmaaktekens in een tekst die als platte tekst gepubliceerd wordt;
 *  - `werbewort` — een woord uit `KI_WERBEWOERTER` (UWG Art. 3); `wort` noemt het.
 */
export const KI_HINWEIS_CODES = [
    'titel_lang',
    'beschreibung_kurz',
    'beschreibung_lang',
    'markdown',
    'werbewort',
];
export const kiHinweisSchema = z
    .object({ code: z.enum(KI_HINWEIS_CODES), wort: z.string().min(1).max(40).optional() })
    .strict();
/** Eén voorgesteld veld: de nabewerkte tekst, de sperre (of `null`) en de hinweise. */
export const kiFeldVorschlagSchema = z
    .object({
    text: z.string().max(20_000),
    sperre: kiSperreSchema.nullable(),
    hinweise: z.array(kiHinweisSchema).max(40),
})
    .strict();
/**
 * Het antwoord van `POST /vermarktung/objekte/:id/ki-vorschlag` (order A5).
 * `quelleGekuerzt`: het invoerplafond heeft een vrije tekst ingekort — het CRM
 * zegt dat erbij. `antwortModell` is `response.model` van de API (of `null`).
 */
export const kiVorschlagAntwortSchema = z
    .object({
    vorschlagId: z.string().uuid(),
    modell: kiModellSchema,
    antwortModell: z.string().min(1).max(120).nullable(),
    promptVersion: z.string().min(1).max(20),
    erstelltAm: z.string().datetime(),
    quelleHash: z.string().regex(/^[0-9a-f]{64}$/),
    quelleGekuerzt: z.boolean(),
    felder: z
        .object({
        titel: kiFeldVorschlagSchema,
        kurzbeschreibung: kiFeldVorschlagSchema,
        beschreibung: kiFeldVorschlagSchema,
    })
        .strict(),
})
    .strict();
/** Waarom de KI niet beschikbaar is (`GET /vermarktung/ki/status`). */
export const KI_STATUS_GRUENDE = ['deaktiviert', 'nicht_konfiguriert'];
/**
 * `GET /vermarktung/ki/status` (order A5): of de knop er mag zijn, welk model de
 * standaard is, en hoeveel van het daglimiet al verbruikt is (alle uitkomsten
 * tellen). Met de vlag uit raakt de server de KI-tabel niet: `verbraucht24h` is
 * dan 0 — het CRM toont het getal alleen bij `verfuegbar: true`.
 */
export const kiStatusSchema = z
    .object({
    verfuegbar: z.boolean(),
    grund: z.enum(KI_STATUS_GRUENDE).optional(),
    standardModell: kiModellSchema,
    modelle: z.array(kiModellSchema).min(1),
    limit: z.number().int().min(0),
    verbraucht24h: z.number().int().min(0),
})
    .strict();
/**
 * De foutcodes van de twee KI-routes (order A5), elk met zijn HTTP-status — het
 * CRM heeft voor elk een zin. `vermarktung_objekt_not_found` is de bestaande
 * 404 van de werkbank.
 */
export const KI_FEHLER_STATUS = Object.freeze({
    ki_deaktiviert: 503,
    ki_nicht_konfiguriert: 503,
    vermarktung_objekt_not_found: 404,
    ki_quelle_unvollstaendig: 422,
    ki_tageslimit_erreicht: 429,
    ki_zeitueberschreitung: 504,
    ki_abgelehnt: 502,
    ki_antwort_ungueltig: 502,
    ki_nicht_verfuegbar: 503,
});
